import {
  getList,
  getObject,
  getOptionalObject,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { SingleChoiceOption, SingleChoiceOptionDTO } from "./singleChoiceOption";
import { Code, CodeDTO } from "./code";
import { SingleChoiceDisplayTypeV1 } from "./singleChoiceDisplayTypeV1";

export interface SingleChoiceQuestionV1DTO {
  options: SingleChoiceOptionDTO[];
  defaultOptionCode?: CodeDTO | null;
  singleChoiceDisplayType: SingleChoiceDisplayTypeV1.SingleChoiceDisplayTypeV1DTO;
  isChoiceResetAllowed?: boolean | null;
  isUnanswered?: boolean | null;
}

export class SingleChoiceQuestionV1 {
  readonly isSingleChoiceQuestionV1SingleChoiceQuestionV1: boolean = true;

  readonly options: SingleChoiceOption[];
  readonly defaultOptionCode: Code | null;
  readonly singleChoiceDisplayType: SingleChoiceDisplayTypeV1.SingleChoiceDisplayTypeV1;
  readonly isChoiceResetAllowed: boolean | null;
  readonly isUnanswered: boolean | null;

  static readonly constraints = {
    "options": {
      "minSize": 1,
      "maxSize": 1024,
    },
  }

  constructor(
  options: SingleChoiceOption[], 
  defaultOptionCode: Code | null = null, 
  singleChoiceDisplayType: SingleChoiceDisplayTypeV1.SingleChoiceDisplayTypeV1, 
  isChoiceResetAllowed: boolean | null = null, 
  isUnanswered: boolean | null = null
  ) {
    SingleChoiceQuestionV1.validateOptions(options);
    this.options = options;
    this.defaultOptionCode = defaultOptionCode;
    this.singleChoiceDisplayType = singleChoiceDisplayType;
    this.isChoiceResetAllowed = isChoiceResetAllowed;
    this.isUnanswered = isUnanswered;
  }

  static fromDTO(dto: SingleChoiceQuestionV1DTO): SingleChoiceQuestionV1 {
    const options = getList<SingleChoiceOptionDTO, SingleChoiceOption>(
      dto,
      "options",
      SingleChoiceOption.fromDTO,
      InvalidSingleChoiceQuestionV1OptionsError
    );
    let defaultOptionCode = getOptionalObject(dto, "defaultOptionCode");
    if(defaultOptionCode !== undefined && defaultOptionCode !== null) {
      defaultOptionCode = Code.fromDTO(getObject(dto, "defaultOptionCode", InvalidSingleChoiceQuestionV1DefaultOptionCodeError));
    }
    const singleChoiceDisplayType = SingleChoiceDisplayTypeV1.fromDTO(getObject(dto, "singleChoiceDisplayType", InvalidSingleChoiceQuestionV1SingleChoiceDisplayTypeError));
    const isChoiceResetAllowed = getOptionalBoolean(dto, "isChoiceResetAllowed", InvalidSingleChoiceQuestionV1IsChoiceResetAllowedError);
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidSingleChoiceQuestionV1IsUnansweredError);
    return new SingleChoiceQuestionV1(
      options,
      defaultOptionCode,
      singleChoiceDisplayType,
      isChoiceResetAllowed,
      isUnanswered
    );
  }

  toDTO(): SingleChoiceQuestionV1DTO {
    const dto: SingleChoiceQuestionV1DTO = {
        options: this.options.map((e: SingleChoiceOption) => { return e.toDTO() }),
        singleChoiceDisplayType: this.singleChoiceDisplayType.toDTO(),
    };
    if (this.defaultOptionCode !== undefined && this.defaultOptionCode !== null){
      dto.defaultOptionCode = this.defaultOptionCode.toDTO();
    }
    if (this.isChoiceResetAllowed !== undefined && this.isChoiceResetAllowed !== null){
      dto.isChoiceResetAllowed = this.isChoiceResetAllowed;
    }
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  private static validateOptions = (options: SingleChoiceOption[]): void => {
    if(options.length < 1) {
      throw new InvalidSingleChoiceQuestionV1OptionsMinSizeError(
        1,
        `Attribute options is too short. Size is ${options.length}. Min size is 1.`
      );
    }
    if(options.length > 1024) {
      throw new InvalidSingleChoiceQuestionV1OptionsMaxSizeError(
        1024,
        `Attribute options is too long. Size is ${options.length}. Max size is 1024.`
      );
    }
  }
  
}

SingleChoiceQuestionV1.prototype.toString = function toString(): string {
  return `SingleChoiceQuestionV1(options='${this.options}',defaultOptionCode='${this.defaultOptionCode}',singleChoiceDisplayType='${this.singleChoiceDisplayType}',isChoiceResetAllowed='${this.isChoiceResetAllowed}',isUnanswered='${this.isUnanswered}')`;
};

export class InvalidSingleChoiceQuestionV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1Error";
  }
}
export class InvalidSingleChoiceQuestionV1OptionsError extends InvalidSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1OptionsError";
  }
}

export class InvalidSingleChoiceQuestionV1OptionsMaxSizeError extends InvalidSingleChoiceQuestionV1OptionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1OptionsMaxSizeError";
  }
}

export class InvalidSingleChoiceQuestionV1OptionsMinSizeError extends InvalidSingleChoiceQuestionV1OptionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1OptionsMinSizeError";
  }
}

export class InvalidSingleChoiceQuestionV1DefaultOptionCodeError extends InvalidSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1DefaultOptionCodeError";
  }
}

export class InvalidSingleChoiceQuestionV1SingleChoiceDisplayTypeError extends InvalidSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1SingleChoiceDisplayTypeError";
  }
}

export class InvalidSingleChoiceQuestionV1IsChoiceResetAllowedError extends InvalidSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1IsChoiceResetAllowedError";
  }
}

export class InvalidSingleChoiceQuestionV1IsUnansweredError extends InvalidSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSingleChoiceQuestionV1IsUnansweredError";
  }
}
